import apiClient from './apiClient';
import configData from '../configure';

/**
 * Fetch paginated attendance records with filters.
 * @param {string} filterType - 'user' or 'class'
 * @param {string} id - userId or classId
 * @param {string} [startDate] - YYYY-MM-DD
 * @param {string} [endDate] - YYYY-MM-DD
 * @param {string} [lastEvaluatedKey] - pagination token
 * @param {number} [pageSize=100] - page size
 */
export const fetchAttendanceRecords = async ({ filterType, id, startDate, endDate, lastEvaluatedKey, pageSize = 100 }) => {
  if (!filterType || !id) throw new Error('Filter type and ID are required');

  const params = { pageSize };
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (lastEvaluatedKey) params.lastEvaluatedKey = lastEvaluatedKey;

  const url = configData.getAttendanceRecords(filterType, id);
  const response = await apiClient.get(url, { params });
  return response.data;
};

/**
 * Fetch all attendance records by handling pagination automatically.
 * @param {string} filterType - 'user' or 'class'
 * @param {string} id - userId or classId
 * @param {string} [startDate] - YYYY-MM-DD
 * @param {string} [endDate] - YYYY-MM-DD
 */
export const fetchAllAttendanceRecords = async ({ filterType, id, startDate, endDate }) => {
  let allItems = [];
  let lastEvaluatedKey = null;
  const pageSize = 100; // Backend enforced limit max is 100

  do {
    const responseData = await fetchAttendanceRecords({
      filterType,
      id,
      startDate,
      endDate,
      lastEvaluatedKey,
      pageSize
    });

    const items = responseData?.items || responseData || [];
    // If responseData is a direct list, we've got everything
    if (Array.isArray(responseData)) {
      return responseData;
    }
    allItems = [...allItems, ...items];
    
    lastEvaluatedKey = responseData?.lastEvaluatedKey || null;
  } while (lastEvaluatedKey && lastEvaluatedKey !== '0');

  return allItems;
};

/**
 * Create a new attendance record.
 * @param {Object} payload - Attendance record matching pojo schema
 */
export const createAttendanceRecord = async (payload) => {
  const response = await apiClient.post(configData.ATTENDANCE_BASE, payload);
  return response.data;
};

/**
 * Update an existing attendance record by ID.
 * @param {string} attendanceId 
 * @param {Object} payload 
 */
export const updateAttendanceRecord = async (attendanceId, payload) => {
  if (!attendanceId) throw new Error('Attendance ID is required for updates');
  const response = await apiClient.put(`${configData.ATTENDANCE_BASE}/${attendanceId}`, payload);
  return response.data;
};

/**
 * Delete an attendance record by ID.
 * @param {string} attendanceId 
 */
export const deleteAttendanceRecord = async (attendanceId) => {
  if (!attendanceId) throw new Error('Attendance ID is required for deletion');
  const response = await apiClient.delete(`${configData.ATTENDANCE_BASE}/${attendanceId}`);
  return response.data;
};
